/**
 * EvidencePanel — WHY two records are connected.
 * Renders the documented basis for one edge: every shared published
 * dossier, profile cross-link notes, associate relations and claims
 * (with their sources). Nothing speculative ever appears here.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Link2, Quote, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { fetchEdge, KIND_META, groupOf, pathForEntity } from "./explorerApi";
import { recordEvidence } from "../../investigation/investigationMemory";
import { RecordThumb } from "../media/PublishedMedia";

const Label = ({ children }) => (
  <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#757065] mt-5 mb-2">
    {children}
  </div>
);

export const EvidencePanel = ({ a, b, onFollow, compact = false }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => { recordEvidence(); }, [a, b]);

  useEffect(() => {
    let dead = false;
    setStatus("loading");
    fetchEdge(a, b)
      .then((d) => { if (!dead) { setData(d); setStatus("ok"); } })
      .catch(() => { if (!dead) setStatus("error"); });
    return () => { dead = true; };
  }, [a, b]);

  if (status === "loading") {
    return (
      <div className="p-6 text-center" data-testid="evidence-loading">
        <Loader2 size={16} className="animate-spin inline text-[#A68F5E]" />
      </div>
    );
  }
  if (status === "error" || !data) {
    return (
      <div className="p-4 font-mono text-[10px] text-[#D92D20]" data-testid="evidence-error">
        No documented connection on record.
      </div>
    );
  }

  const ctx = data.profile_context || {};
  const notes = [data.a, data.b]
    .map((n) => ({ who: n, c: ctx[n.slug] || {} }))
    .filter((x) => x.c.crosslink_note || x.c.associate_relation || x.c.claims);

  return (
    <div className={compact ? "" : "p-4"} data-testid={`evidence-panel-${a}-${b}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <RecordThumb targetId={data.a.slug}
                     className="w-7 h-9 object-contain border border-[#221F1C] bg-[#0B0A0D] shrink-0" />
        <span className="font-heading text-base text-[#E4DFD3]">{data.a.name}</span>
        <span className="text-[#D92D20] font-mono text-[10px]">×</span>
        <RecordThumb targetId={data.b.slug}
                     className="w-7 h-9 object-contain border border-[#221F1C] bg-[#0B0A0D] shrink-0" />
        <span className="font-heading text-base text-[#E4DFD3]">{data.b.name}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <span className="font-mono text-[8px] uppercase tracking-[0.25em] px-1.5 py-0.5"
              style={{ background: data.strength === "corroborated" ? "#3FB950" : "#A68F5E",
                       color: "#0a0907" }}
              data-testid="edge-strength">
          {data.strength}
        </span>
        <span className="font-mono text-[9px] text-[#757065]">
          co-appearance in {data.shared_dossiers.length} published dossier{data.shared_dossiers.length === 1 ? "" : "s"}
        </span>
      </div>

      <Label>Documented in</Label>
      <div className="space-y-1.5">
        {data.shared_dossiers.map((d) => (
          <Link key={d.slug} to={`/topic/${d.slug}`}
                className="flex items-start gap-2 border p-2 hover:border-[#D92D20] transition-colors"
                style={{ borderColor: "#2D2A26", background: "#0d0c0a" }}
                data-testid={`evidence-dossier-${d.slug}`}>
            <FileText size={11} className="text-[#A68F5E] mt-0.5 shrink-0" />
            <span className="min-w-0">
              <span className="block text-[12px] text-[#E4DFD3] leading-tight">{d.title}</span>
              {d.category && (
                <span className="block font-mono text-[8px] uppercase tracking-[0.2em] text-[#5A554C] mt-0.5">
                  {d.category}
                </span>
              )}
            </span>
          </Link>
        ))}
      </div>

      {notes.length > 0 && (
        <>
          <Label>Profile context</Label>
          <div className="space-y-2">
            {notes.map(({ who, c }) => (
              <div key={who.slug} className="border-l-2 pl-2.5"
                   style={{ borderColor: KIND_META[groupOf(who)]?.color || "#A68F5E" }}>
                <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#757065]">
                  from {who.name}'s file
                </div>
                {c.crosslink_note && (
                  <div className="text-[11px] text-[#C9C2B4] mt-1 leading-relaxed flex gap-1.5">
                    <Link2 size={10} className="mt-0.5 shrink-0 text-[#A68F5E]" />{c.crosslink_note}
                  </div>
                )}
                {c.associate_relation && (
                  <div className="text-[11px] text-[#C9C2B4] mt-1 leading-relaxed flex gap-1.5">
                    <ShieldCheck size={10} className="mt-0.5 shrink-0 text-[#A68F5E]" />{c.associate_relation}
                  </div>
                )}
                {(c.claims || []).map((cl, i) => (
                  <div key={i} className="mt-2">
                    <div className="text-[11px] text-[#C9C2B4] leading-relaxed flex gap-1.5">
                      <Quote size={10} className="mt-0.5 shrink-0 text-[#D92D20]" />
                      <span>
                        {cl.statement}
                        <span className="block font-mono text-[8px] uppercase tracking-[0.2em] text-[#A68F5E] mt-0.5">
                          {cl.category}
                        </span>
                      </span>
                    </div>
                    {(cl.sources || []).map((s, j) => (
                      <a key={j} href={s.url} target="_blank" rel="noreferrer"
                         className="block font-mono text-[9px] text-[#58A6FF] hover:underline truncate ml-4 mt-0.5">
                        {s.title || s.url}
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}

      {onFollow && (
        <div className="mt-5 flex flex-wrap gap-2">
          {[data.a, data.b].map((n) => (
            <button key={n.slug} type="button" onClick={() => onFollow(n.slug)}
                    className="inline-flex items-center gap-1.5 border px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-[#A68F5E] hover:text-[#D92D20] hover:border-[#D92D20] transition-colors"
                    style={{ borderColor: "#3A3630" }}
                    data-testid={`follow-btn-${n.slug}`}>
              Follow {n.name} <ArrowRight size={10} />
            </button>
          ))}
        </div>
      )}
      <div className="mt-3 flex gap-3">
        <Link to={pathForEntity(data.a)} className="font-mono text-[9px] text-[#757065] hover:text-[#E4DFD3] transition-colors underline underline-offset-2">
          open {data.a.name}'s file
        </Link>
        <Link to={pathForEntity(data.b)} className="font-mono text-[9px] text-[#757065] hover:text-[#E4DFD3] transition-colors underline underline-offset-2">
          open {data.b.name}'s file
        </Link>
      </div>
    </div>
  );
};
